import residencesService from '@/services/residencesService'

const state = {
  residences: [],
  housingUnits: [],
  availableHouses: [],
  availableHousesByUnit: [],
  pendingRequests: [],
  myPendingHouseRequests: [],
  selectedHousingUnit: null,
  loading: false,
  error: null,
  activeResidence: null
}

const getters = {
  getResidences: state => state.residences,
  getHousingUnits: state => state.housingUnits,
  getAvailableHouses: state => state.availableHouses,
  getAvailableHousesByUnit: state => state.availableHousesByUnit,
  getPendingRequests: state => state.pendingRequests,
  getMyPendingHouseRequests: state => state.myPendingHouseRequests,
  getSelectedHousingUnit: state => state.selectedHousingUnit,
  isLoading: state => state.loading,
  getError: state => state.error,
  getActiveResidence: state => state.activeResidence,
  getUnhabitedHouses: state => state.availableHouses.filter(house => !house.inhabited),
  getUnhabitedHousesByUnit: state => state.availableHousesByUnit.filter(house => !house.inhabited)
}

const actions = {
  async fetchMyResidences ({ commit }) {
    commit('SET_LOADING', true)
    try {
      const residences = await residencesService.getMyResidences()
      const activeResidence = residences.find(r => r.is_active_context)
      commit('SET_RESIDENCES', residences)
      commit('SET_ACTIVE_RESIDENCE', activeResidence)
      commit('SET_ERROR', null)
    } catch (error) {
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchHousingUnits ({ commit }) {
    commit('SET_LOADING', true)
    try {
      const units = await residencesService.getHousingUnits()
      commit('SET_HOUSING_UNITS', units)
      commit('SET_ERROR', null)
    } catch (error) {
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAvailableHousesByUnit ({ commit }, idHousingUnit) {
    commit('SET_LOADING', true)
    try {
      const houses = await residencesService.getAvailableHousesByUnit(idHousingUnit)
      commit('SET_AVAILABLE_HOUSES_BY_UNIT', houses)
      commit('SET_SELECTED_HOUSING_UNIT', idHousingUnit)
      commit('SET_ERROR', null)
    } catch (error) {
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAvailableHouses ({ commit }) {
    commit('SET_LOADING', true)
    try {
      const houses = await residencesService.getAvailableHouses()
      commit('SET_AVAILABLE_HOUSES', houses)
      commit('SET_ERROR', null)
    } catch (error) {
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchPendingRequests ({ commit }) {
    commit('SET_LOADING', true)
    try {
      const requests = await residencesService.getPendingRequests()
      commit('SET_PENDING_REQUESTS', requests)
      commit('SET_ERROR', null)
    } catch (error) {
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchMyPendingHouseRequests ({ commit }) {
    commit('SET_LOADING', true)
    try {
      const requests = await residencesService.getPendingHouseRequests()
      commit('SET_MY_PENDING_HOUSE_REQUESTS', requests)
      commit('SET_ERROR', null)
    } catch (error) {
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async switchResidence ({ commit }, idResident) {
    commit('SET_LOADING', true)
    try {
      await residencesService.switchResidence(idResident)
      await this.dispatch('fetchMyResidences')
      commit('SET_ERROR', null)
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async requestAddHouse ({ commit }, idHouse) {
    commit('SET_LOADING', true)
    try {
      const response = await residencesService.requestAddHouse(idHouse)
      commit('SET_ERROR', null)
      return response
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || error.message
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async validateAddHouse ({ commit }, { residentId, action, feedback }) {
    commit('SET_LOADING', true)
    try {
      const response = await residencesService.validateAddHouse(residentId, action, feedback)
      await this.dispatch('fetchPendingRequests')
      commit('SET_ERROR', null)
      return response
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const mutations = {
  SET_RESIDENCES (state, residences) {
    state.residences = Array.isArray(residences) ? residences : []
  },
  SET_HOUSING_UNITS (state, units) {
    state.housingUnits = Array.isArray(units) ? units : []
  },
  SET_AVAILABLE_HOUSES (state, houses) {
    state.availableHouses = Array.isArray(houses) ? houses : []
  },
  SET_AVAILABLE_HOUSES_BY_UNIT (state, houses) {
    state.availableHousesByUnit = Array.isArray(houses) ? houses : []
  },
  SET_PENDING_REQUESTS (state, requests) {
    state.pendingRequests = Array.isArray(requests) ? requests : []
  },
  SET_MY_PENDING_HOUSE_REQUESTS (state, requests) {
    state.myPendingHouseRequests = Array.isArray(requests) ? requests : []
  },
  SET_SELECTED_HOUSING_UNIT (state, unitId) {
    state.selectedHousingUnit = unitId
  },
  SET_LOADING (state, loading) {
    state.loading = loading
  },
  SET_ERROR (state, error) {
    state.error = error
  },
  SET_ACTIVE_RESIDENCE (state, residence) {
    state.activeResidence = residence
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
